// searchTags.js
// Tag search helpers for hashtag mentions

import {apiCreateTag, apiSearchTags, apiSuggestTagsBySegmentId} from "../../../../utils/api/tags";

/**
 * Search tags for hashtag suggestions
 * @param {string} query - Search query
 * @param {string} docId - Optional document ID
 * @param {Object} options - Search options
 * @param {number} options.limit - Maximum results (default: 10)
 * @returns {Promise<Array>} Array of tag objects with normalized fields
 */
export async function searchTagsForMention(query, docId, options = {}) {
    const limit = options.limit || 10;

    console.log('[searchTagsForMention] Starting search:', {query, docId, limit});

    try {
        const res = await apiSearchTags(query || '', docId);

        console.log('[searchTagsForMention] API response:', {
            query,
            resultCount: Array.isArray(res) ? res.length : 0,
            rawResult: res,
        });

        // Normalize to array of {id, name}
        const normalized = (Array.isArray(res) ? res : [])
            .map(it => {
                if (!it) return null;
                if (typeof it === 'string') return {
                    id: it.toLowerCase(),
                    name: it,
                    label: it,
                };
                const name = (it.name || it.tag || '').toString();
                const id = (it.tagId || it.id || name).toString();
                return name ? {id, name, label: name} : null;
            })
            .filter(Boolean);

        // Always offer to create the tag if user typed something
        const q = (query || '').trim();
        if (q) {
            // Check if the query exactly matches an existing tag (case-insensitive)
            const exactMatch = normalized.find(tag =>
                (tag.name || tag.label || '').toLowerCase() === q.toLowerCase()
            );

            // Add create option to results (unless exact match exists)
            const result = normalized.slice(0, limit);

            if (!exactMatch) {
                console.log('[searchTagsForMention] Adding create option for:', q);
                result.push({
                    id: `create:${q}`,
                    name: q,
                    label: q,
                    __create: true,
                    display: `Create tag "${q}"`,
                });
            }

            console.log('[searchTagsForMention] Returning results:', {
                query,
                count: result.length,
                tags: result.map(t => t.name || t.label),
                hasCreateOption: !exactMatch,
            });
            return result;
        }

        // If no query, just return the normalized results
        const result = normalized.slice(0, limit);
        console.log('[searchTagsForMention] Returning results (no query):', {
            count: result.length,
            tags: result.map(t => t.name || t.label),
        });
        return result;
    } catch (err) {
        console.error('[searchTagsForMention] Error:', err);

        // On error, still allow creating the tag if query provided
        const q = (query || '').trim();
        if (q) {
            return [{
                id: `create:${q}`,
                name: q,
                label: q,
                __create: true,
                display: `Create tag "${q}"`,
            }];
        }

        // If no query, just return empty array (better than showing create option for empty string)
        return [];
    }
}

/**
 * Create a new tag
 * @param {string} name - Tag name
 * @param {string} docId - Optional document ID
 * @returns {Promise<Object>} Created tag object
 */
export async function createTagForMention(name, docId) {
    const normalizedName = (name || '').trim().toLowerCase();
    try {
        const created = await apiCreateTag({name: normalizedName, docId: docId || undefined});
        const createdName = (created?.name || created?.tag || normalizedName).toString();
        const createdId = (created?.id || created?.tagId || createdName).toString();

        return {
            id: createdId,
            name: createdName,
            label: createdName,
        };
    } catch (err) {
        console.error('[createTagForMention] Error:', err);

        // If creation fails, still return the tag so user isn't blocked
        return {
            id: normalizedName,
            name: normalizedName,
            label: normalizedName,
        };
    }
}

/**
 * Format tag data for display in suggestion list
 * @param {Object} tag - Tag object
 * @returns {Object} Formatted display data
 */
export function formatTagForDisplay(tag) {
    if (tag.__create) {
        return {
            title: tag.display || `Create tag "${tag.label || tag.name}"`,
            subtitle: '',
            isCreate: true,
        };
    }

    return {
        title: tag.label || tag.name,
        subtitle: '',
        isCreate: false,
    };
}

/**
 * Get AI-suggested tags for a segment
 * Uses the same API as the SuggestedTags component
 * @param {string} segmentId - Segment ID
 * @param {Object} options - Options
 * @param {boolean} options.includeNewSuggestions - Include new tag suggestions (default: true)
 * @returns {Promise<Object>} Object with {existingTags: [], newTags: []}
 */
export async function getSuggestedTagsForSegment(segmentId, options = {}) {
    const includeNewSuggestions = options.includeNewSuggestions !== false;

    try {
        if (!segmentId) {
            return {existingTags: [], newTags: []};
        }

        const response = await apiSuggestTagsBySegmentId({
            segmentId,
            includeNewSuggestions
        });

        const {existingTags = [], newTags = []} = response.segments?.[0] || {};

        // Normalize tags to consistent format
        const normalizeTag = (t) => {
            const label = (t?.name || t?.tag || '').toString().trim();
            if (!label) return null;
            return {
                id: t?.tagId || t?.id || label.toLowerCase(),
                name: label,
                label: label,
            };
        };

        return {
            existingTags: existingTags.map(normalizeTag).filter(Boolean),
            newTags: newTags.map(normalizeTag).filter(Boolean),
        };
    } catch (err) {
        console.error('[getSuggestedTagsForSegment] Error:', err);
        return {existingTags: [], newTags: []};
    }
}

/**
 * Search tags with optional AI suggestions
 * Combines manual search with AI-powered suggestions
 * @param {string} query - Search query
 * @param {string} docId - Optional document ID
 * @param {Object} options - Search options
 * @param {number} options.limit - Maximum results (default: 10)
 * @param {string} options.segmentId - Optional segment ID for AI suggestions
 * @param {boolean} options.includeSuggestions - Include AI suggestions (default: false)
 * @returns {Promise<Array>} Combined array of tags
 */
export async function searchTagsWithSuggestions(query, docId, options = {}) {
    const limit = options.limit || 10;
    const segmentId = options.segmentId;
    const includeSuggestions = options.includeSuggestions === true;

    try {
        // Get manual search results
        const searchResults = await searchTagsForMention(query, docId, {limit});

        // If no query and suggestions requested, get AI suggestions
        if ((!query || !query.trim()) && includeSuggestions && segmentId) {
            const {existingTags, newTags} = await getSuggestedTagsForSegment(segmentId);

            // Combine and deduplicate
            const allTags = [...existingTags, ...newTags, ...searchResults];
            const seen = new Set();
            const unique = [];

            for (const tag of allTags) {
                const key = tag.id || tag.label || tag.name;
                if (!seen.has(key.toLowerCase())) {
                    seen.add(key.toLowerCase());
                    unique.push(tag);
                }
            }

            return unique.slice(0, limit);
        }

        return searchResults;
    } catch (err) {
        console.error('[searchTagsWithSuggestions] Error:', err);
        return await searchTagsForMention(query, docId, {limit});
    }
}



